/**
 * Utility function to find the most recent policy from an array of policies
 *
 * Sorting priority:
 * 1. issuedAt (most relevant for insurance policies)
 * 2. createdAt (when the policy record was created)
 * 3. updatedAt (when the policy was last modified)
 * 4. id (for consistent ordering if all dates are missing)
 */

interface PolicyWithDates {
  id: string;
  issuedAt?: Date | string | null;
  createdAt?: Date | string | null;
  updatedAt?: Date | string | null;
  [key: string]: any;
}

/**
 * Safely parse a date value that could be Date, string, or null
 */
const parseDate = (date: Date | string | null | undefined): number => {
  if (!date) return 0;
  if (date instanceof Date) return date.getTime();
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

/**
 * Get the most relevant date from a policy for sorting
 * Priority: issuedAt > createdAt > updatedAt
 */
const getPolicySortDate = (policy: PolicyWithDates): number => {
  const issuedAt = parseDate(policy.issuedAt);
  if (issuedAt > 0) return issuedAt;

  const createdAt = parseDate(policy.createdAt);
  if (createdAt > 0) return createdAt;

  const updatedAt = parseDate(policy.updatedAt);
  return updatedAt;
};

/**
 * Sort policies by most recent first
 * Returns a new sorted array without mutating the original
 */
export const sortPoliciesByMostRecent = <T extends PolicyWithDates>(
  policies: T[]
): T[] => {
  if (!policies || policies.length === 0) {
    return [];
  }

  return [...policies].sort((a, b) => {
    const dateA = getPolicySortDate(a);
    const dateB = getPolicySortDate(b);

    // Sort descending (most recent first)
    if (dateB !== dateA) {
      return dateB - dateA;
    }

    // If dates are equal, sort by ID for consistency
    return b.id.localeCompare(a.id);
  });
};

/**
 * Get the most recent policy from an array
 * Returns null if array is empty or null
 */
export const getMostRecentPolicy = <T extends PolicyWithDates>(
  policies: T[] | null | undefined
): T | null => {
  if (!policies || policies.length === 0) {
    return null;
  }

  if (policies.length === 1) {
    return policies[0];
  }

  const sorted = sortPoliciesByMostRecent(policies);
  return sorted[0];
};

/**
 * Log policy sorting information for debugging
 */
export const logPolicySort = (policies: PolicyWithDates[], context: string) => {
  // Disabled for production
  /*
  console.log(`${context}: Policy sorting debug`, {
    totalPolicies: policies.length,
    policies: policies.map(p => ({
      id: p.id,
      issuedAt: p.issuedAt,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
      sortDate: new Date(getPolicySortDate(p)).toISOString(),
    })),
  });
  */
};
